import { useLocation } from 'react-router-dom';
import useSiteLeadStore from '@store/siteLeadStore';
import {
	isProvisionalSiteVisitRecordingSiteId,
	PROVISIONAL_NEW_SITE_RECORDING_ID,
} from '@services/siteVisitRecording/siteVisitRecordingConstants';
import { isRecordingAccessBde } from '@config/constant';
import { useGetRxDb } from '@hooks/getRxdbData';
import { ProfileData } from '@services/hooks/profile';

/** Existing-site visit flow: site id from store or navigation state. */
export function useSiteVisitRecordingEligibility(): {
	recordingEnabled: boolean;
	siteId: number | null;
	isNewSiteRecordingFlow: boolean;
} {
	const { siteId: storeSiteId, addNewSiteLead, siteVisitId } = useSiteLeadStore();
	const { state: locationState } = useLocation();
	const { result: profile } = useGetRxDb<ProfileData>('profile');
	const hasRecordingAccessBde = isRecordingAccessBde(profile[0]?.mdicode_c);

	const locationSiteId =
		locationState && typeof locationState === 'object' && 'siteId' in locationState
			? (locationState as { siteId?: number | null }).siteId ?? null
			: null;

	const isNewSiteRecordingFlow =
		Boolean(
			locationState &&
				typeof locationState === 'object' &&
				(locationState as { newSiteRecording?: boolean }).newSiteRecording
		) ||
		(storeSiteId != null && isProvisionalSiteVisitRecordingSiteId(storeSiteId));

	const resolvedSiteId = storeSiteId ?? locationSiteId ?? null;
	/** Draft visit completion ("Add New Site Visit") uses an existing site + visit id. */
	const isDraftSiteVisitFlow = siteVisitId != null;

	/**
	 * Contractor 360 "+" (new site) starts recording before a real site id exists.
	 * Use provisional id until Task List creates the site lead.
	 */
	const siteId = resolvedSiteId ?? (isNewSiteRecordingFlow ? PROVISIONAL_NEW_SITE_RECORDING_ID : null);

	const recordingEnabled =
		hasRecordingAccessBde &&
		siteId != null &&
		(isProvisionalSiteVisitRecordingSiteId(siteId) || !addNewSiteLead || isDraftSiteVisitFlow);

	return { recordingEnabled, siteId, isNewSiteRecordingFlow };
}
